import {useContext, useEffect} from "react";
import {observer} from "mobx-react";
import {CalendarSmall, Loader, YStack} from "@chayka/ui";
import type {BookingWizardSlideComponent} from "./types";
import {HoursButtons, ParkingSlider} from "@/components/Booking";
import {BookingStateContext, type TRoom} from "../state";
import {trpc} from "@/app/trpc";
import {useQuery} from "@tanstack/react-query";
import {MAX_DATE, MIN_DATE} from "../constants";

export const ParkingSlide: BookingWizardSlideComponent = observer(
  ({setNextText, setNextDisabled}) => {
    const {
      date,
      setDate,
      subType,
      parkingSpot,
      setParkingSpot,
      parkingHours,
      setParkingHours,
      carNumber,
      setCarNumber,
      carModel,
      setCarModel,
    } = useContext(BookingStateContext);

    const {data: parkingSpots, isLoading: isLoadingParkingSpots} = useQuery(
      trpc.booking.roomsBySubType.queryOptions({subTypeCode: "parking"}),
    );

    useEffect(() => {
      if (parkingSpots?.[0]) {
        setParkingSpot(parkingSpots[0]);
      }
    }, [parkingSpots, setParkingSpot]);

    const {data: parkingScheduleData, isLoading: isLoadingParkingSchedule} = useQuery({
      ...trpc.booking.roomSchedule.queryOptions({roomId: parkingSpot?.id || -1, date}),
      enabled: Boolean(parkingSpot?.id),
    });

    const isParkingWizard = subType?.code === "parking";

    useEffect(() => {
      setNextText?.(parkingHours.length || isParkingWizard ? "Далее" : "Парковка не нужна");
    }, [parkingHours, setNextText, isParkingWizard]);

    useEffect(() => {
      const isParkingWizardWithoutSelectedHours = isParkingWizard && !parkingHours.length;
      const isHoursSelectedWithoutCarInfo = parkingHours.length > 0 && (!carNumber || !carModel);

      setNextDisabled?.(isParkingWizardWithoutSelectedHours || isHoursSelectedWithoutCarInfo);
    }, [carModel, carNumber, parkingHours, setNextDisabled, isParkingWizard]);

    const handleChangeParkingSpot = (spot: TRoom | null) => {
      setParkingSpot(spot);
      setParkingHours([]);
    };

    if (isLoadingParkingSpots) {
      return (
        <div className="h-full w-full flex justify-center items-center">
          <Loader />
        </div>
      );
    }

    return (
      <YStack className="min-h-0 flex-1 gap-6">
        <div className="shrink-0">
          <ParkingSlider
            parkingSpots={parkingSpots || []}
            selectedParkingSpot={parkingSpot}
            onParkingSpotChange={handleChangeParkingSpot}
            carNumber={carNumber}
            setCarNumber={setCarNumber}
            carModel={carModel}
            setCarModel={setCarModel}
          />
        </div>

        <div className="shrink-0">
          <CalendarSmall
            minDate={isParkingWizard ? MIN_DATE : date}
            maxDate={isParkingWizard ? MAX_DATE : date}
            value={date}
            onChange={isParkingWizard ? setDate : undefined}
          />
        </div>

        {isLoadingParkingSchedule && (
          <div className="h-32 w-full flex justify-center items-center">
            <Loader />
          </div>
        )}

        {!isLoadingParkingSchedule && (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-26 -mt-6 no-scrollbar">
            <div className="mt-6">
              <HoursButtons
                schedule={parkingScheduleData?.timeSlots || []}
                selectedHours={parkingHours}
                setHours={setParkingHours}
              />
            </div>
          </div>
        )}
      </YStack>
    );
  },
);
